import {
  ConfirmPreliminaryOrderResult,
  CreateOrderInput,
  CreatedOrder,
} from '../model/create-order.model';
import {
  OrderPdfStatus,
  OrderReservationStatus,
  OrderStatus,
} from '../model/order-status.model';
import { UpdateOrderResult } from '../model/update-order.model';

export type ApplyCreatedOrdersDefaults = Pick<
  CreateOrderInput,
  | 'reservationNumber'
  | 'orderObservation'
  | 'scheduledPaymentDate'
  | 'commission'
>;

/** Comisión (10%) — el % siempre, también si se ingresó un monto fijo. */
export function formatOrderCommissionLabel(input: {
  commissionPercent?: number | null;
  commissionAmount?: number | null;
  baseAmount?: number | null;
}): string {
  const percent =
    input.commissionPercent != null &&
    Number.isFinite(Number(input.commissionPercent))
      ? Number(input.commissionPercent)
      : (() => {
          const base = Number(input.baseAmount);
          const amount = Number(input.commissionAmount);
          if (Number.isFinite(base) && base > 0 && Number.isFinite(amount)) {
            return (amount / base) * 100;
          }
          return null;
        })();
  if (percent == null) return 'Comisión';
  const rounded = Math.round(percent * 100) / 100;
  const label = Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded);
  return `Comisión (${label}%)`;
}

/**
 * Formats a Date object into YYYY-MM-DD string.
 *
 * Description:
 *   Converts date to ISO calendar date string (YYYY-MM-DD).
 *
 * Parameters:
 *   targetDate: Date - Date object to format.
 *
 * Return Value:
 *   string - Formatted date string.
 *
 * Exceptions & Errors:
 *   None.
 */
export function formatScheduledPaymentDate(targetDate: Date): string {
  const yearString = targetDate.getFullYear();
  const monthString = String(targetDate.getMonth() + 1).padStart(2, '0');
  const dayString = String(targetDate.getDate()).padStart(2, '0');
  return `${yearString}-${monthString}-${dayString}`;
}

/**
 * Converts a Date object into UTC midnight ISO string for order creation.
 *
 * Description:
 *   Produces YYYY-MM-DDT00:00:00.000Z.
 *
 * Parameters:
 *   targetDate: Date - Date object.
 *
 * Return Value:
 *   string - ISO string at UTC midnight.
 *
 * Exceptions & Errors:
 *   None.
 */
export function toScheduledPaymentDateIso(targetDate: Date): string {
  return `${formatScheduledPaymentDate(targetDate)}T00:00:00.000Z`;
}

/**
 * Parses a scheduled payment date string into a Date object without timezone shift.
 *
 * Description:
 *   Safely parses YYYY-MM-DD or ISO strings into calendar Date.
 *
 * Parameters:
 *   rawDateString: string | null | undefined - Date string to parse.
 *
 * Return Value:
 *   Date | null - Parsed Date or null if empty/invalid.
 *
 * Exceptions & Errors:
 *   None.
 */
export function parseScheduledPaymentDateForInput(
  rawDateString: string | null | undefined,
): Date | null {
  if (!rawDateString?.trim()) {
    return null;
  }

  const trimmedString = rawDateString.trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedString)) {
    const [parsedYear, parsedMonth, parsedDay] = trimmedString
      .split('-')
      .map(Number);
    return new Date(parsedYear, parsedMonth - 1, parsedDay);
  }

  if (trimmedString.includes('T')) {
    const [parsedYear, parsedMonth, parsedDay] = trimmedString
      .split('T')[0]
      .split('-')
      .map(Number);
    return new Date(parsedYear, parsedMonth - 1, parsedDay);
  }

  const genericParsedDate = new Date(trimmedString);
  return Number.isNaN(genericParsedDate.getTime()) ? null : genericParsedDate;
}

/**
 * Formats a scheduled payment date string for table UI display (DD/MM/YYYY).
 *
 * Description:
 *   Formats date to DD/MM/YYYY or returns hyphen fallback.
 *
 * Parameters:
 *   rawDateString: string | null | undefined - Input date string.
 *
 * Return Value:
 *   string - Formatted date or fallback text.
 *
 * Exceptions & Errors:
 *   None.
 */
export function formatScheduledPaymentDateDisplay(
  rawDateString: string | null | undefined,
): string {
  if (!rawDateString?.trim()) {
    return '-';
  }

  const parsedDate = parseScheduledPaymentDateForInput(rawDateString);
  if (!parsedDate) {
    return rawDateString;
  }

  const dayString = String(parsedDate.getDate()).padStart(2, '0');
  const monthString = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const yearString = parsedDate.getFullYear();
  return `${dayString}/${monthString}/${yearString}`;
}

/**
 * Extracts orderId from a service record supporting varied entity shapes.
 *
 * Description:
 *   Checks order.orderId, orderId, ordenServicioId.
 *
 * Parameters:
 *   serviceRecord: { order?: { orderId?: string | null } | null; orderId?: string | null; ordenServicioId?: string | null } - Service entity.
 *
 * Return Value:
 *   string | null - Resolved orderId or null.
 *
 * Exceptions & Errors:
 *   None.
 */
export function getServiceOrderId(serviceRecord: {
  order?: { orderId?: string | null } | null;
  orderId?: string | null;
  ordenServicioId?: string | null;
}): string | null {
  return (
    serviceRecord?.order?.orderId ||
    serviceRecord?.orderId ||
    serviceRecord?.ordenServicioId ||
    null
  );
}

/**
 * Extracts orderNumber from a service record supporting varied entity shapes.
 *
 * Description:
 *   Checks order.orderNumber, orderNumber, numOS.
 *
 * Parameters:
 *   serviceRecord: { order?: { orderNumber?: string | number | null } | null; orderNumber?: string | number | null; numOS?: string | number | null } - Service entity.
 *
 * Return Value:
 *   string | number | null - Resolved orderNumber or null.
 *
 * Exceptions & Errors:
 *   None.
 */
export function getServiceOrderNumber(serviceRecord: {
  order?: {
    orderNumber?: string | number | null;
    displayCode?: string | null;
    orderStatus?: string | null;
  } | null;
  orderNumber?: string | number | null;
  numOS?: string | number | null;
}): string | number | null {
  const displayCode = serviceRecord?.order?.displayCode?.trim();
  if (displayCode) {
    return displayCode;
  }
  const orderNumber =
    serviceRecord?.order?.orderNumber ??
    serviceRecord?.orderNumber ??
    serviceRecord?.numOS ??
    null;
  if (
    orderNumber != null &&
    serviceRecord?.order?.orderStatus === 'PRELIMINAR'
  ) {
    return `P-${orderNumber}`;
  }
  return orderNumber;
}

/**
 * Compares two order number values for equality handling numeric/string types.
 *
 * Description:
 *   Trims and compares string representations.
 *
 * Parameters:
 *   firstValue: string | number | null | undefined - First order number.
 *   secondValue: string | number | null | undefined - Second order number.
 *
 * Return Value:
 *   boolean - True if both values represent the same order number.
 *
 * Exceptions & Errors:
 *   None.
 */
export function sameOrderNumberValue(
  firstValue: string | number | null | undefined,
  secondValue: string | number | null | undefined,
): boolean {
  if (firstValue == null || secondValue == null) {
    return false;
  }
  return String(firstValue).trim() === String(secondValue).trim();
}

/**
 * Filters service records matching a specific order number.
 *
 * Description:
 *   Returns all services in the collection that have a matching order number.
 *
 * Parameters:
 *   servicesCollection: Array<any> - Array of service records to filter.
 *   targetOrderNumber: string | number | null | undefined - The order number to match.
 *
 * Return Value:
 *   Array<any> - Sub-array of matching services.
 *
 * Exceptions & Errors:
 *   None.
 */
export function filterServicesByOrderNumber(
  servicesCollection: Array<any>,
  targetOrderNumber: string | number | null | undefined,
): Array<any> {
  if (
    targetOrderNumber == null ||
    targetOrderNumber === '' ||
    !Array.isArray(servicesCollection)
  ) {
    return [];
  }

  return servicesCollection.filter((serviceItem) => {
    const itemOrderNumber = getServiceOrderNumber(serviceItem);
    return sameOrderNumberValue(itemOrderNumber, targetOrderNumber);
  });
}

/**
 * Filters service records matching a specific order ID.
 *
 * Description:
 *   Returns all services in the collection that have a matching orderId.
 *
 * Parameters:
 *   servicesCollection: Array<any> - Array of service records to filter.
 *   targetOrderId: string | null | undefined - The order ID to match.
 *
 * Return Value:
 *   Array<any> - Sub-array of matching services.
 *
 * Exceptions & Errors:
 *   None.
 */
export function filterServicesByOrderId(
  servicesCollection: Array<any>,
  targetOrderId: string | null | undefined,
): Array<any> {
  if (!targetOrderId || !Array.isArray(servicesCollection)) {
    return [];
  }

  return servicesCollection.filter((serviceItem) => {
    const itemOrderId = getServiceOrderId(serviceItem);
    return itemOrderId === targetOrderId;
  });
}

/**
 * Applies newly created orders to local service collections in memory.
 *
 * Description:
 *   Matches service ID against createdOrders and assigns structured order payload.
 *
 * Parameters:
 *   servicesCollection: Array<{ id?: string; [key: string]: unknown }> - In-memory array of services.
 *   createdOrdersList: CreatedOrder[] | null | undefined - Array of created order payloads.
 *   identifierKeyName: string - Field name holding service UUID ('id' by default).
 *   defaultInputValues: ApplyCreatedOrdersDefaults | null | undefined - Optional fallback values submitted in dialog.
 *
 * Return Value:
 *   void.
 *
 * Exceptions & Errors:
 *   None.
 */
export function applyCreatedOrdersToServices(
  servicesCollection: Array<any>,
  createdOrdersList: CreatedOrder[] | null | undefined,
  identifierKeyName: string = 'id',
  defaultInputValues?: ApplyCreatedOrdersDefaults | null,
): void {
  if (!Array.isArray(servicesCollection) || !Array.isArray(createdOrdersList)) {
    return;
  }
  if (!createdOrdersList.length) {
    return;
  }

  for (const serviceRecord of servicesCollection) {
    const serviceIdentifier = String(
      serviceRecord?.[identifierKeyName] ?? '',
    ).trim();
    if (!serviceIdentifier) {
      continue;
    }

    const matchedCreatedOrder = createdOrdersList.find((createdOrder) =>
      (createdOrder.serviceIds || []).some(
        (targetServiceId) =>
          String(targetServiceId).trim() === serviceIdentifier,
      ),
    );

    if (!matchedCreatedOrder) {
      continue;
    }

    const nextStatus =
      matchedCreatedOrder.orderStatus ?? OrderStatus.CONFIRMADO;
    serviceRecord.order = {
      orderId: matchedCreatedOrder.orderId,
      orderNumber: matchedCreatedOrder.orderNumber,
      displayCode:
        matchedCreatedOrder.displayCode ||
        (nextStatus === OrderStatus.PRELIMINAR
          ? `P-${matchedCreatedOrder.orderNumber}`
          : matchedCreatedOrder.orderNumber),
      orderStatus: nextStatus,
      reservationNumber:
        matchedCreatedOrder.reservationNumber ??
        defaultInputValues?.reservationNumber ??
        null,
      reservationStatus: OrderReservationStatus.CONFIRMADO,
      orderObservation:
        matchedCreatedOrder.orderObservation ??
        defaultInputValues?.orderObservation ??
        null,
      scheduledPaymentDate:
        matchedCreatedOrder.scheduledPaymentDate ??
        defaultInputValues?.scheduledPaymentDate ??
        null,
      freezeDeadlineAt: matchedCreatedOrder.freezeDeadlineAt ?? null,
      pdfStatus:
        matchedCreatedOrder.pdfStatus ??
        (nextStatus === OrderStatus.PRELIMINAR
          ? OrderPdfStatus.NOT_APPLICABLE
          : OrderPdfStatus.PENDING),
      pdfGeneratedAt: null,
      lastPdfError: null,
      commissionAmount: matchedCreatedOrder.commissionAmount ?? null,
      commissionPercent: matchedCreatedOrder.commissionPercent ?? null,
    };
    serviceRecord.orderId = matchedCreatedOrder.orderId;
    serviceRecord.ordenServicioId = matchedCreatedOrder.orderId;
    serviceRecord.ordenServicioStatus = nextStatus;
    serviceRecord.orderIssuanceFrozen =
      nextStatus !== OrderStatus.PRELIMINAR;
  }
}

/**
 * Clears order information across service items matching a deleted order ID.
 *
 * Description:
 *   Sets order to null and removes orderId/ordenServicioId fields for matching services.
 *
 * Parameters:
 *   servicesCollection: Array<any> - List of service items to clean.
 *   deletedOrderIdString: string - The ID of the deleted order.
 *
 * Return Value:
 *   void.
 *
 * Exceptions & Errors:
 *   None.
 */
export function clearServiceOrderById(
  servicesCollection: Array<any>,
  deletedOrderIdString: string,
): void {
  if (!deletedOrderIdString || !Array.isArray(servicesCollection)) {
    return;
  }

  for (const serviceItem of servicesCollection) {
    const activeOrderId = getServiceOrderId(serviceItem);
    if (activeOrderId === deletedOrderIdString) {
      serviceItem.order = null;
      serviceItem.orderId = undefined;
      serviceItem.ordenServicioId = undefined;
      serviceItem.ordenServicioStatus = undefined;
    }
  }
}

/**
 * Clears order information across service items matching a deleted order number.
 *
 * Description:
 *   Sets order to null and removes orderId/ordenServicioId fields for matching services.
 *
 * Parameters:
 *   servicesCollection: Array<any> - List of service items to clean.
 *   deletedOrderNumber: string | number | null | undefined - The order number of the deleted order.
 *
 * Return Value:
 *   void.
 *
 * Exceptions & Errors:
 *   None.
 */
export function clearServiceOrdersByOrderNumber(
  servicesCollection: Array<any>,
  deletedOrderNumber: string | number | null | undefined,
): void {
  if (!deletedOrderNumber || !Array.isArray(servicesCollection)) {
    return;
  }

  for (const serviceItem of servicesCollection) {
    const itemOrderNumber = getServiceOrderNumber(serviceItem);
    if (sameOrderNumberValue(itemOrderNumber, deletedOrderNumber)) {
      serviceItem.order = null;
      serviceItem.orderId = undefined;
      serviceItem.ordenServicioId = undefined;
      serviceItem.ordenServicioStatus = undefined;
    }
  }
}

/**
 * Result payload returned upon closing the Delete Order dialog.
 */
export interface DeleteOrderDialogResult {
  success: boolean;
  shouldRefresh?: boolean;
  orderNumber?: string | number | null;
  orderId?: string | null;
  order?: Partial<UpdateOrderResult> | null;
}

/**
 * Applies the deletion result to in-memory services upon dialog close.
 *
 * Description:
 *   Cleans up order records matching either orderId or orderNumber.
 *
 * Parameters:
 *   servicesCollection: Array<any> - List of services.
 *   dialogResultPayload: DeleteOrderDialogResult | null | undefined - Dialog close result.
 *   fallbackTargetService: any - Optional fallback service to clean.
 *
 * Return Value:
 *   void.
 *
 * Exceptions & Errors:
 *   None.
 */
export function applyDeleteOrderDialogResult(
  servicesCollection: Array<any>,
  dialogResultPayload: DeleteOrderDialogResult | null | undefined,
  fallbackTargetService?: any,
): void {
  if (!dialogResultPayload?.success) {
    return;
  }

  if (dialogResultPayload.orderId) {
    clearServiceOrderById(servicesCollection, dialogResultPayload.orderId);
  }

  if (dialogResultPayload.orderNumber) {
    clearServiceOrdersByOrderNumber(
      servicesCollection,
      dialogResultPayload.orderNumber,
    );
  }

  if (fallbackTargetService) {
    fallbackTargetService.order = null;
    fallbackTargetService.orderId = undefined;
    fallbackTargetService.ordenServicioId = undefined;
    fallbackTargetService.ordenServicioStatus = undefined;
  }
}

/**
 * Applies the result of an order mutation (such as updateOrderScheduledPaymentDate, updateOrderReservationStatus, etc.)
 * across all service rows sharing the same orderNumber or orderId.
 *
 * Description:
 *   Finds all matching services in the collection by orderNumber or orderId, and merges the update
 *   payload into each item's order property as well as corresponding top-level order properties.
 *
 * Parameters:
 *   servicesCollection: Array<any> - Collection of service records in memory.
 *   orderNumber: string | number | null | undefined - The order number to match.
 *   updatePayload: Partial<UpdateOrderResult> | null | undefined - Mutation result payload to merge.
 *   fallbackServiceRecord: any - Optional fallback service to update if no matching order is found.
 *
 * Return Value:
 *   void.
 *
 * Exceptions & Errors:
 *   None.
 */
export function applyOrderUpdateToServices(
  servicesCollection: Array<any>,
  orderNumber: string | number | null | undefined,
  updatePayload: Partial<UpdateOrderResult> | null | undefined,
  fallbackServiceRecord?: any,
): void {
  if (!updatePayload) {
    return;
  }

  let targetServicesList = orderNumber
    ? filterServicesByOrderNumber(servicesCollection, orderNumber)
    : [];

  if (!targetServicesList.length && updatePayload.orderId) {
    targetServicesList = filterServicesByOrderId(
      servicesCollection,
      updatePayload.orderId,
    );
  }

  if (!targetServicesList.length && fallbackServiceRecord) {
    targetServicesList = [fallbackServiceRecord];
  }

  for (const targetServiceItem of targetServicesList) {
    if (!targetServiceItem) {
      continue;
    }

    targetServiceItem.order = {
      ...(targetServiceItem.order ?? {}),
      ...updatePayload,
    };

    if (updatePayload.orderId) {
      targetServiceItem.orderId = updatePayload.orderId;
      targetServiceItem.ordenServicioId = updatePayload.orderId;
    }
    if (updatePayload.orderNumber != null) {
      targetServiceItem.orderNumber = updatePayload.orderNumber;
      targetServiceItem.numOS = updatePayload.orderNumber;
    }
    if (updatePayload.orderStatus) {
      targetServiceItem.ordenServicioStatus = updatePayload.orderStatus;
    }
  }
}

/**
 * Al confirmar una OS preliminar se actualizan TODAS las filas de esa OS.
 */
export function applyConfirmedPreliminaryOrder(
  services: any[],
  result: ConfirmPreliminaryOrderResult | null | undefined,
): void {
  if (!result) {
    return;
  }

  const matches = (order: any): boolean =>
    Boolean(order) &&
    ((Boolean(result.orderId) &&
      order.orderId != null &&
      String(order.orderId) === String(result.orderId)) ||
      sameOrderNumberValue(order.orderNumber, result.orderNumber));

  const patch = (order: any): void => {
    order.orderStatus = result.orderStatus;
    if (result.displayCode) {
      order.displayCode = result.displayCode;
    } else if (result.orderNumber != null) {
      order.displayCode = String(result.orderNumber);
    }
    order.pdfStatus = result.pdfStatus;
  };

  for (const item of services ?? []) {
    if (matches(item?.order)) {
      patch(item.order);
      item.ordenServicioStatus = result.orderStatus;
      item.orderStatus = result.orderStatus;
    }
  }
}

/**
 * Al editar «Confirmar antes del» se actualiza la fecha en TODAS las filas de esa OS.
 */
export function applyFreezeDeadlineToServices(
  services: any[],
  orderRef: {
    orderId?: string | null;
    orderNumber?: string | number | null;
  } | null | undefined,
  freezeDeadlineAt: string | null | undefined,
): void {
  if (!orderRef || !freezeDeadlineAt) {
    return;
  }

  const matches = (order: any): boolean =>
    Boolean(order) &&
    ((Boolean(orderRef.orderId) &&
      order.orderId != null &&
      String(order.orderId) === String(orderRef.orderId)) ||
      sameOrderNumberValue(order.orderNumber, orderRef.orderNumber));

  for (const item of services ?? []) {
    if (matches(item?.order)) {
      item.order.freezeDeadlineAt = freezeDeadlineAt;
    }
  }
}
